/**
 * Insurance Arc
 *
 * Question: "How is this covered?"
 *
 * Entry: route → insuranceCardEntry
 * Exit: done (final) — produces InsuranceOutput
 *
 * Actors: analyzeOcr, verifyInsurance
 * Signals: GO_BACK (from insuranceCardEntry when not in storefront mode)
 *
 * Navigation map: see README.md
 */

import { assign, sendParent, setup } from "xstate";
import type {
  InsuranceInput,
  InsuranceOutput,
} from "../contracts/arc-contracts";
import {
  deriveEligibilityFailureType,
  derivePendingPricesMode,
  isInsuranceValid,
  mapRepriceToInsuranceState,
} from "../helper/insurance-helpers";
import type { KioskMachineContext, KioskMachineEvent } from "../types";

// Actors
import { analyzeOcrActor } from "../actors/analyze-ocr.actor";
import { verifyInsuranceActor } from "../actors/verify-insurance.actor";

import {
  isDobError,
  isMemberIdError,
  isNameError,
  isOONInsurance,
  isSecondaryInsuranceError,
  isSecondaryMemberIdError,
} from "../guards/insurance-error.guard";
import {
  createInsuranceInputSnapshot,
  hasInsuranceInputChanged,
  hasInsuranceInputChangedWithEvent,
  isAtLeastPrimaryInsuranceActive,
} from "../guards/insurance.guard";
import { hasSecondaryInsurance } from "../guards/secondary-insurance.guard";
// Guards
import { hasCompletedBefore } from "../helper/arc-helpers";

import {
  clearInsurance,
  clearInsuredPricing,
  clearSecondaryInsurance,
  restoreInsuranceIdsFromBackground,
} from "../actions/clear-insurance.action";
import { saveActorResult } from "../actions/save-actor-result.action";
import { saveError } from "../actions/save-error.action";
// Actions
import { saveFormData } from "../actions/save-form-data.action";
import { savePhotoData } from "../actions/save-photo-data.action";
import { isQrMode } from "../guards";

function parseSubscriberName(
  subscriberName: string | undefined,
  fallbackFirst: string,
  fallbackLast: string,
): { first: string; last: string } {
  if (!subscriberName?.trim())
    return { first: fallbackFirst, last: fallbackLast };
  const parts = subscriberName.trim().split(/\s+/);
  return {
    first: parts[0],
    last: parts.slice(1).join(" ") || fallbackLast,
  };
}

/**
 * Seed arc context from input.
 */
function createInsuranceContext(input: InsuranceInput): KioskMachineContext {
  if (input.previousOutput) {
    return {
      orgId: input.orgId,
      consumerId: input.consumerId,
      firstName: input.firstName,
      lastName: input.lastName,
      dateOfBirth: input.dateOfBirth,
      email: input.email,
      addingInsuranceFromStorefront: input.addingInsuranceFromStorefront,
      qrMode: input.qrMode,
      ...input.previousOutput,
      _reInvoked: true,
      // Restore internal routing field from output so route state recognizes auto-verified success
      ...(input.previousOutput.autoVerifiedSuccess && {
        _savedInsuranceOutcome: "success",
      }),
    };
  }

  // Return user with saved insurance on file
  if (input.savedInsurance?.primaryInsurance) {
    const outcome = input.savedInsurance.savedInsuranceOutcome;
    const savedPrimary = input.savedInsurance.primaryInsurance;
    const savedSecondary = input.savedInsurance.secondaryInsurance;

    return {
      orgId: input.orgId,
      consumerId: input.consumerId,
      firstName: input.firstName,
      lastName: input.lastName,
      dateOfBirth: input.dateOfBirth,
      email: input.email,
      addingInsuranceFromStorefront: input.addingInsuranceFromStorefront,
      qrMode: input.qrMode,
      insurances: {
        primaryInsurance: savedPrimary,
        secondaryInsurance: savedSecondary,
      },
      // Seed insuranceStates from reprice result so downstream pricing guards
      // (isAtLeastPrimaryInsuranceActive) see an active primary after the
      // eligibility-failure screen's "Continue with primary" path, which
      // otherwise leaves insuranceStates undefined and forces CASH pricing.
      insuranceStates: {
        primary: mapRepriceToInsuranceState(savedPrimary),
        ...(savedSecondary && {
          secondary: mapRepriceToInsuranceState(savedSecondary),
        }),
      },
      primaryInsurancePhotoFront:
        input.savedInsurance.primaryInsurancePhotoFront,
      secondaryInsurancePhotoFront:
        input.savedInsurance.secondaryInsurancePhotoFront,
      _hasSavedInsurance: true,
      _savedInsuranceOutcome: outcome,
      // Pre-resolved eligibility failure
      ...(outcome === "failure" &&
        (() => {
          const primaryValid = savedPrimary
            ? isInsuranceValid(savedPrimary)
            : false;
          const checkingFor = primaryValid ? "secondary" : "primary";
          const failingIns = primaryValid ? savedSecondary : savedPrimary;

          const failureType = failingIns
            ? deriveEligibilityFailureType(failingIns)
            : "inactive";

          const failureFields = {
            _savedEligibilityFailed: true,
            _eligibilityCheckingFor: checkingFor,
            _eligibilityFailureType: failureType,
            pendingPricesMode: failureType === "unverified",
          };
          return failureFields;
        })()),
    };
  }

  return {
    orgId: input.orgId,
    consumerId: input.consumerId,
    firstName: input.firstName,
    lastName: input.lastName,
    dateOfBirth: input.dateOfBirth,
    email: input.email,
    addingInsuranceFromStorefront: input.addingInsuranceFromStorefront,
    qrMode: input.qrMode,
  };
}

/**
 * Extract typed output from arc context at final state.
 */
function extractInsuranceOutput(context: KioskMachineContext): InsuranceOutput {
  return {
    insurances: context.insurances,
    insuranceStates: context.insuranceStates,
    insuranceInputSnapshot: context.insuranceInputSnapshot,
    pendingPricesMode: context.pendingPricesMode,
    primaryInsurancePhotoFront: context.primaryInsurancePhotoFront,
    primaryInsurancePhotoBack: context.primaryInsurancePhotoBack,
    secondaryInsurancePhotoFront: context.secondaryInsurancePhotoFront,
    secondaryInsurancePhotoBack: context.secondaryInsurancePhotoBack,
    ocrPrimaryInsurance: context.ocrPrimaryInsurance,
    ocrSecondaryInsurance: context.ocrSecondaryInsurance,
    firstName: context.firstName,
    lastName: context.lastName,
    dateOfBirth: context.dateOfBirth,
    returnToStorefront: context._returnToStorefront === true,
    autoVerifiedSuccess: context._savedInsuranceOutcome === "success",
    primaryInsuranceBackground: context.primaryInsuranceBackground as
      | InsuranceOutput["primaryInsuranceBackground"]
      | undefined,
    secondaryInsuranceBackground: context.secondaryInsuranceBackground as
      | InsuranceOutput["secondaryInsuranceBackground"]
      | undefined,
  };
}

/**
 * Derive failure type from insurance error state (insuranceVerificationFailure screen).
 * Mirrors the component's getFailureType logic for the FRONT_DESK signal.
 */
function deriveFrontDeskFailureType(
  context: KioskMachineContext,
): "oon" | "inactive" | "unverified" {
  const error = (context.insuranceError || context.error) as
    | {
        code?: string;
        raw?: { code?: string };
        insuranceStates?: {
          primary?: { active?: boolean };
        };
        insuranceStatuses?: Array<{ status?: string }>;
      }
    | undefined;

  if (
    error?.code === "OUT_OF_NETWORK" ||
    error?.raw?.code === "OUT_OF_NETWORK"
  ) {
    return "oon";
  }

  const primaryStatus = error?.insuranceStatuses?.[0]?.status;
  if (primaryStatus === "inactive") return "inactive";

  return "unverified";
}

// ─── Shared Actions ───────────────────────────────────────────

/** Error payload shape produced by insurance verification actors. Exported for testing. */
export type InsuranceVerificationError = {
  code: string;
  insurances?: {
    primaryInsurance?: { status?: string };
    secondaryInsurance?: { status?: string };
  };
  /** Per-insurance verification results. When present, used to stamp only the
   *  insurance(s) that are actually out-of-network instead of both. */
  insuranceStates?: {
    primary?: {
      inNetwork: boolean;
      active: boolean;
      withBenefits: boolean;
      verified: boolean;
    };
    secondary?: {
      inNetwork: boolean;
      active: boolean;
      withBenefits: boolean;
      verified: boolean;
    };
  };
  insuranceStatuses?: Array<{ status?: string }>;
};

type ContextInsurances =
  | {
      primaryInsurance?: Record<string, unknown>;
      secondaryInsurance?: Record<string, unknown>;
    }
  | undefined;

/**
 * Pure computation extracted from assignInsuranceErrorToContext. Exported for testing.
 *
 * Derives the context update to apply when an insurance verification actor fails.
 * Key behaviors:
 * - OON error with insuranceStates: stamps only the OON insurance as ACTIVE_OUT_OF_NETWORK
 * - OON error without insuranceStates: stamps both (fallback)
 * - Non-OON error: spreads error.insurances per-insurance status
 * - Always resets insuranceInputSnapshot and addingInsuranceFromStorefront
 * - Preserves insuranceStates from error for clearSecondaryInsurance recovery
 * - Derives pendingPricesMode from unverified/active_benefits_not_returned statuses
 */
export function computeInsuranceErrorContext(
  contextInsurances: ContextInsurances,
  error: InsuranceVerificationError | undefined,
) {
  const insurances = (() => {
    if (!error?.code) return contextInsurances;
    const isOON = error.code === "OUT_OF_NETWORK";
    // When OON, stamp only the insurance(s) that are actually out-of-network.
    // Use per-insurance inNetwork flags from insuranceStates when available so
    // that an in-network primary is not incorrectly marked OON when only the
    // secondary is OON (and vice versa). Fall back to stamping both when
    // insuranceStates is absent.
    const primaryOON =
      isOON &&
      (error.insuranceStates
        ? error.insuranceStates.primary?.inNetwork === false
        : true);
    const secondaryOON =
      isOON &&
      (error.insuranceStates
        ? error.insuranceStates.secondary?.inNetwork === false
        : true);
    // The `error` field is what the downstream pricing filters use to drop
    // an insurance from the pricing call (see KioskEventContext.tsx:251 and
    // medrite-checkin.machine.ts:1598 — both filter on
    // `error === OUT_OF_NETWORK`). Apply the same per-insurance condition
    // as `status`, so an in-network primary on a secondary-only OON failure
    // doesn't get filtered out and lose its insured pricing.
    //
    // Fallback (no insuranceStates) and non-OON errors still stamp both, to
    // preserve the pre-PR-#4404 behavior exercised by existing tests.
    const stampPrimaryError = !isOON || !error.insuranceStates || primaryOON;
    const stampSecondaryError =
      !isOON || !error.insuranceStates || secondaryOON;
    return {
      primaryInsurance: contextInsurances?.primaryInsurance
        ? {
            ...contextInsurances.primaryInsurance,
            ...(stampPrimaryError ? { error: error.code } : {}),
            ...(primaryOON
              ? { status: "ACTIVE_OUT_OF_NETWORK" }
              : { ...error.insurances?.primaryInsurance }),
          }
        : undefined,
      secondaryInsurance: contextInsurances?.secondaryInsurance
        ? {
            ...contextInsurances.secondaryInsurance,
            ...(stampSecondaryError ? { error: error.code } : {}),
            ...(secondaryOON
              ? { status: "ACTIVE_OUT_OF_NETWORK" }
              : { ...error.insurances?.secondaryInsurance }),
          }
        : undefined,
    };
  })();

  return {
    insuranceInputSnapshot: undefined,
    addingInsuranceFromStorefront: false,
    insurances,
    // Preserve insuranceStates so clearSecondaryInsurance can recover primary state
    // on the "continue with primary only" path after a secondary OON/inactive failure.
    ...(error?.insuranceStates && { insuranceStates: error.insuranceStates }),
    pendingPricesMode: error?.insuranceStatuses?.some((s) => {
      const status = s?.status?.toLowerCase();
      return (
        status === "unverified" || status === "active_benefits_not_returned"
      );
    }),
  };
}

// biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
const assignInsuranceErrorToContext = assign(({ event, context }: any) => {
  return computeInsuranceErrorContext(
    context.insurances as ContextInsurances,
    event.error as InsuranceVerificationError | undefined,
  );
});

// ─── Machine Definition ──────────────────────────────────────

export const insuranceArc = setup({
  types: {
    context: {} as KioskMachineContext,
    events: {} as KioskMachineEvent,
    input: {} as InsuranceInput,
    output: {} as InsuranceOutput,
  },
  actors: {
    analyzeOcr: analyzeOcrActor.impl,
    verifyInsurance: verifyInsuranceActor.impl,
  },
  guards: {
    hasSecondaryInsurance: hasSecondaryInsurance.impl,
    hasCompletedBefore,
    isNameError: isNameError.impl,
    isDobError: isDobError.impl,
    isOON: isOONInsurance.impl,
    isMemberIdError: isMemberIdError.impl,
    isSecondaryMemberIdError: isSecondaryMemberIdError.impl,
    isSecondaryInsuranceError: isSecondaryInsuranceError.impl,
    isQrMode: isQrMode.impl,
  },
  actions: {
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveFormData: saveFormData.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveError: saveError.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveActorResult: saveActorResult.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    savePhotoData: savePhotoData.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    clearInsurance: clearInsurance.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    clearSecondaryInsurance: clearSecondaryInsurance.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    clearInsuredPricing: clearInsuredPricing.impl as any,
    restoreInsuranceIdsFromBackground:
      // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
      restoreInsuranceIdsFromBackground.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    assignInsuranceErrorToContext: assignInsuranceErrorToContext as any,
  },
}).createMachine({
  id: "insurance",
  context: ({ input }) => createInsuranceContext(input),
  output: ({ context }) => extractInsuranceOutput(context),
  initial: "route",

  states: {
    // ===== ENTRY ROUTING =====
    route: {
      always: [
        {
          // Reprice returned success → still run real eligibility check
          // (reprice only validates card data, not patient identity)
          guard: ({ context }) =>
            context._savedInsuranceOutcome === "success" &&
            context.addingInsuranceFromStorefront !== true,
          target: "checkingEligibility",
          actions: [assign({ _eligibilityCheckingFor: "primary" })],
        },
        {
          // Re-invocation: if primary insurance already exists with photos, skip to confirm form
          guard: ({ context }) => {
            const insurances = context.insurances as
              | { primaryInsurance?: { insuranceId?: string } }
              | undefined;
            return (
              !!insurances?.primaryInsurance?.insuranceId &&
              !!context.primaryInsurancePhotoFront &&
              !!context.primaryInsurancePhotoBack
            );
          },
          target: "inputForm",
        },
        {
          // Pre-resolved eligibility failure → show EligibilityFailure directly
          guard: ({ context }) => context._savedEligibilityFailed === true,
          target: "eligibilityFailure",
          actions: [
            assign(({ context }) => {
              if (context._eligibilityFailureType !== "oon") return {};
              // Stamp OON status now, mirroring what assignInsuranceErrorToContext does
              // in the real-time path. Without this, context.insurances retains the raw
              // reprice status ("active") and the DB write after "use anyway" is wrong.
              return computeInsuranceErrorContext(
                context.insurances as ContextInsurances,
                {
                  code: "OUT_OF_NETWORK",
                  insuranceStates: context.insuranceStates,
                },
              );
            }),
          ],
        },
        {
          // Return user with saved insurance → confirm saved insurance
          guard: ({ context }) => context._hasSavedInsurance === true,
          target: "savedPrimaryConfirm",
        },
        { target: "insuranceCardEntry" },
      ],
    },

    // ===== SAVED INSURANCE CONFIRMATION (return users) =====

    savedPrimaryConfirm: {
      meta: {
        componentName: "SavedInsuranceConfirm",
        contentKey: "medrite.checkin.saved-primary-confirm",
        metadata: {
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: [
          {
            // Yes → run eligibility check (mock pass-through for now)
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event }: any) => event.data?.confirmed === true,
            target: "checkingEligibility",
            actions: [assign({ _eligibilityCheckingFor: "primary" })],
          },
          {
            // No + has saved secondary → clear primary, ask about secondary
            guard: ({ context }) => !!context.insurances?.secondaryInsurance,
            target: "savedSecondaryConfirm",
            actions: [
              assign({
                insurances: ({ context }) => ({
                  ...context.insurances,
                  primaryInsurance: undefined,
                }),
                primaryInsurancePhotoFront: undefined,
                primaryInsurancePhotoBack: undefined,
              }),
            ],
          },
          {
            // No + no secondary → clear primary, go to normal flow
            target: "insuranceCardEntry",
            actions: [
              assign({
                insurances: ({ context }) => ({
                  ...context.insurances,
                  primaryInsurance: undefined,
                }),
                primaryInsurancePhotoFront: undefined,
                primaryInsurancePhotoBack: undefined,
                _hasSavedInsurance: undefined,
              }),
            ],
          },
        ],
        BACK: [
          {
            guard: ({ context }) =>
              context.addingInsuranceFromStorefront === true,
            target: "done",
            actions: [
              assign({
                _returnToStorefront: true,
                addingInsuranceFromStorefront: false,
              }),
            ],
          },
          {
            actions: sendParent({ type: "GO_BACK" }),
          },
        ],
      },
    },

    savedSecondaryConfirm: {
      meta: {
        componentName: "SavedInsuranceConfirm",
        contentKey: "medrite.checkin.saved-secondary-confirm",
        metadata: {
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: [
          {
            // Yes → proceed to eligibility / verification / done
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event }: any) => event.data?.confirmed === true,
            target: "checkingEligibility",
            actions: [
              assign({
                _secondaryConfirmed: true,
                _eligibilityCheckingFor: "secondary",
              }),
            ],
          },
          {
            // No → clear secondary, go to secondary card entry
            target: "secondaryInsuranceCardEntry",
            actions: [
              assign({
                insurances: ({ context }) => ({
                  ...context.insurances,
                  secondaryInsurance: undefined,
                }),
                secondaryInsurancePhotoFront: undefined,
                secondaryInsurancePhotoBack: undefined,
              }),
            ],
          },
        ],
        BACK: [
          {
            // Reprice-success path: user never visited savedPrimaryConfirm → exit arc
            guard: ({ context }) =>
              context._savedInsuranceOutcome === "success",
            actions: sendParent({ type: "GO_BACK" }),
          },
          { target: "savedPrimaryConfirm" },
        ],
      },
    },

    checkingEligibility: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.checking-eligibility",
      },
      invoke: {
        id: "checkEligibility",
        src: "verifyInsurance",
        input: ({ context }) => {
          const insurances = context.insurances as
            | {
                primaryInsurance?: {
                  payerId: string;
                  payer: string;
                  memberId: string;
                  state: string;
                  subscriberName?: string;
                  insuredFirstName?: string;
                  insuredLastName?: string;
                };
                secondaryInsurance?: {
                  payerId: string;
                  payer: string;
                  memberId: string;
                  state: string;
                  subscriberName?: string;
                  insuredFirstName?: string;
                  insuredLastName?: string;
                };
              }
            | undefined;

          // Only verify the insurance tier being checked
          const isPrimary = context._eligibilityCheckingFor === "primary";
          const insuranceList = isPrimary
            ? [insurances?.primaryInsurance].filter(Boolean)
            : [
                insurances?.primaryInsurance,
                insurances?.secondaryInsurance,
              ].filter(Boolean);

          return {
            consumerId: context.consumerId,
            firstName: context.firstName,
            lastName: context.lastName,
            dateOfBirth: context.dateOfBirth,
            insurances: insuranceList.map((ins) => {
              const parsed = parseSubscriberName(
                ins?.subscriberName,
                context.firstName,
                context.lastName,
              );
              return {
                ...ins,
                insuredFirstName: ins?.insuredFirstName ?? parsed.first,
                insuredLastName: ins?.insuredLastName ?? parsed.last,
              };
            }),
            orgId: context.orgId,
            email: context.email,
          };
        },
        onDone: [
          {
            // Has saved secondary that hasn't been confirmed yet → ask about it
            guard: ({ context }) =>
              !!context.insurances?.secondaryInsurance &&
              context._secondaryConfirmed !== true &&
              context._hasSavedInsurance === true,
            target: "savedSecondaryConfirm",
            actions: [
              "saveActorResult",
              assign({
                insuranceStates: ({ event }) => {
                  const output = event.output as
                    | { insuranceStates?: unknown }
                    | undefined;
                  return output?.insuranceStates || [];
                },
              }),
            ],
          },
          {
            // All confirmed → done
            target: "done",
            actions: [
              "saveActorResult",
              assign({
                insuranceStates: ({ event }) => {
                  const output = event.output as
                    | { insuranceStates?: unknown }
                    | undefined;
                  return output?.insuranceStates || [];
                },
                pendingPricesMode: ({ event }) => {
                  const output = event.output as
                    | {
                        insuranceStates?: {
                          primary?: { withBenefits?: boolean };
                        };
                        pendingPricesMode?: boolean;
                      }
                    | undefined;
                  return derivePendingPricesMode(
                    output?.insuranceStates,
                    output?.pendingPricesMode,
                  );
                },
              }),
            ],
          },
        ],
        onError: [
          {
            // OON, inactive, or unverified → failure screen
            // For saved insurance we skip the unverified review flow and go straight to failure
            target: "insuranceVerificationFailure",
            actions: ["saveError", "assignInsuranceErrorToContext"],
          },
        ],
      },
    },

    eligibilityFailure: {
      meta: {
        componentName: "EligibilityFailure",
        contentKey: "medrite.checkin.eligibility-failure",
      },
      on: {
        BACK: [
          {
            // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
            guard: ({ context }: any) =>
              context.addingInsuranceFromStorefront === true,
            target: "done",
            actions: [
              assign({
                _eligibilityFailureType: undefined,
                _returnToStorefront: true,
                addingInsuranceFromStorefront: false,
              }),
            ],
          },
          {
            // Pre-resolved failure → go back to parent (intaking arc)
            actions: [
              assign({ _eligibilityFailureType: undefined }),
              sendParent({ type: "GO_BACK" }),
            ],
          },
        ],
        SUBMIT: [
          {
            // Go to front desk → signal parent to route to front-desk completion screen
            // (same as insuranceVerificationFailure's FRONT_DESK handler)
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event }: any) => event.data?.action === "frontDesk",
            actions: sendParent(({ context }) => ({
              type: "FRONT_DESK",
              data: {
                ...extractInsuranceOutput(context),
                failureType: context._eligibilityFailureType ?? "unverified",
              },
            })),
          },
          {
            // Continue without insurance → clear all insurance, self-pay
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event }: any) => event.data?.action === "continueWithout",
            target: "done",
            actions: [
              "clearInsurance",
              assign({
                _hasSavedInsurance: undefined,
                _savedEligibilityFailed: undefined,
                _eligibilityFailureType: undefined,
                _eligibilityCheckingFor: undefined,
                pendingPricesMode: false,
              }),
            ],
          },
          {
            // Enter new secondary insurance
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event, context }: any) =>
              event.data?.action === "enterNew" &&
              context._eligibilityCheckingFor === "secondary",
            target: "secondaryInsuranceCardEntry",
            actions: [
              "clearSecondaryInsurance",
              assign({
                _hasSavedInsurance: undefined,
                _savedEligibilityFailed: undefined,
                _eligibilityFailureType: undefined,
                _eligibilityCheckingFor: undefined,
              }),
            ],
          },
          {
            // Enter new primary insurance → go to card entry (clear the failing insurance)
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event }: any) => event.data?.action === "enterNew",
            target: "insuranceCardEntry",
            actions: [
              "clearInsurance",
              assign({
                _hasSavedInsurance: undefined,
                _savedEligibilityFailed: undefined,
                _eligibilityFailureType: undefined,
                _eligibilityCheckingFor: undefined,
              }),
            ],
          },
          {
            // Use it anyway → OON/inactive insurance, proceed as self-pay
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event }: any) => event.data?.action === "useAnyway",
            target: "done",
            actions: [
              assign({
                _savedEligibilityFailed: undefined,
                _eligibilityFailureType: undefined,
                _eligibilityCheckingFor: undefined,
                pendingPricesMode: false,
              }),
            ],
          },
          {
            // Next → proceed; only enter pending-prices mode for unverified failures
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event }: any) => event.data?.action === "next",
            target: "done",
            actions: [
              // biome-ignore lint/suspicious/noExplicitAny: XState context typing
              assign(({ context }: any) => ({
                _savedEligibilityFailed: undefined,
                _eligibilityFailureType: undefined,
                _eligibilityCheckingFor: undefined,
                pendingPricesMode:
                  context._eligibilityFailureType === "unverified",
              })),
            ],
          },
          {
            // Continue with primary only → clear secondary, self-pay
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            guard: ({ event }: any) => event.data?.action === "continuePrimary",
            target: "done",
            actions: [
              "clearSecondaryInsurance",
              assign({
                _hasSavedInsurance: undefined,
                _savedEligibilityFailed: undefined,
                _eligibilityFailureType: undefined,
                _eligibilityCheckingFor: undefined,
                pendingPricesMode: false,
              }),
            ],
          },
        ],
      },
    },

    // ===== PRIMARY INSURANCE INPUT (flattened from insuranceInput compound) =====

    insuranceCardEntry: {
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.insurance-card-entry",
        metadata: {
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: [
          {
            // QR mode with physical card → upload flow
            guard: ({ context, event }) =>
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              isQrMode.impl({ context, event } as any),
            target: "uploadPrimaryInsuranceFrontPhoto",
            actions: ["saveFormData"],
          },
          {
            // Kiosk mode (or no physical card) → capture flow
            target: "frontPhoto",
            actions: ["saveFormData"],
          },
        ],
        SKIP: {
          target: "done",
          actions: ["clearInsurance"],
        },
        BACK: [
          {
            // If adding from storefront, complete arc with returnToStorefront flag
            guard: ({ context }) =>
              context.addingInsuranceFromStorefront === true,
            target: "done",
            actions: [
              assign({
                _returnToStorefront: true,
                addingInsuranceFromStorefront: false,
              }),
            ],
          },
          {
            // Arc boundary: send GO_BACK signal to parent
            actions: sendParent({ type: "GO_BACK" }),
          },
        ],
      },
    },

    uploadPrimaryInsuranceFrontPhoto: {
      meta: {
        componentName: "PhotoUpload",
        contentKey: "medrite.checkin.upload-insurance-photo",
        metadata: {
          cardType: "primaryInsurance",
          side: "front",
        },
      },
      on: {
        CONFIRM: {
          target: "uploadPrimaryInsuranceBackPhoto",
          actions: ["savePhotoData"],
        },
        TAKE_PHOTO: {
          target: "frontPhoto",
        },
        SKIP: {
          target: "insuranceCardMemberIdForm",
        },
        BACK: {
          target: "insuranceCardEntry",
        },
      },
    },

    uploadPrimaryInsuranceBackPhoto: {
      meta: {
        componentName: "PhotoUpload",
        contentKey: "medrite.checkin.upload-insurance-photo",
        metadata: {
          cardType: "primaryInsurance",
          side: "back",
        },
      },
      on: {
        CONFIRM: {
          target: "processingOcrInsurance",
          actions: ["savePhotoData"],
        },
        TAKE_PHOTO: {
          target: "backPhoto",
        },
        SKIP: {
          target: "insuranceCardMemberIdForm",
        },
        BACK: {
          target: "uploadPrimaryInsuranceFrontPhoto",
        },
      },
    },

    frontPhoto: {
      meta: {
        componentName: "PhotoCapture",
        contentKey: "medrite.checkin.photo-capture",
        metadata: {
          cardType: "primaryInsurance",
          side: "front",
          autoSubmit: true,
        },
      },
      on: {
        CONFIRM: {
          target: "backPhoto",
          actions: ["savePhotoData"],
        },
        SKIP: {
          target: "insuranceCardMemberIdForm",
        },
        BACK: [
          {
            // In QR mode with physical card, go back to upload screen
            guard: ({ context, event }) =>
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              isQrMode.impl({ context, event } as any),
            target: "uploadPrimaryInsuranceFrontPhoto",
          },
          {
            // If primary insurance already exists (re-invocation skipped question),
            // send GO_BACK to parent
            guard: ({ context }) => {
              const insurances = context.insurances as
                | { primaryInsurance?: { insuranceId?: string } }
                | undefined;
              return !!insurances?.primaryInsurance?.insuranceId;
            },
            actions: sendParent({ type: "GO_BACK" }),
          },
          {
            target: "insuranceCardEntry",
          },
        ],
      },
    },

    backPhoto: {
      meta: {
        componentName: "PhotoCapture",
        contentKey: "medrite.checkin.photo-capture",
        metadata: {
          cardType: "primaryInsurance",
          side: "back",
          autoSubmit: true,
        },
      },
      on: {
        CONFIRM: {
          target: "processingOcrInsurance",
          actions: ["savePhotoData"],
        },
        SKIP: {
          target: "insuranceCardMemberIdForm",
        },
        BACK: [
          {
            // In QR mode, go back to the upload screen
            guard: ({ context, event }) =>
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              isQrMode.impl({ context, event } as any),
            target: "uploadPrimaryInsuranceBackPhoto",
          },
          { target: "frontPhoto" },
        ],
      },
    },

    processingOcrInsurance: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.processing-insurance",
      },
      invoke: {
        id: "analyzeOcr",
        src: "analyzeOcr",
        input: ({ context }) => ({
          mode: "insurance" as const,
          imageBase64: context.primaryInsurancePhotoFront,
          contextKey: "ocrPrimaryInsurance" as const,
          orgId: context.orgId,
          queries: [
            "What is the payer name?",
            "What is the member ID?",
            "What is the subscriber name?",
          ],
        }),
        onDone: {
          target: "saveInsuranceOcrToContext",
          actions: [
            "saveActorResult",
            // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
            assign(({ event }: any) => {
              const ocrData = event.output?.ocrPrimaryInsurance;
              return {
                ocrPrimaryBoundingBoxes: ocrData?.boundingBoxes ?? null,
              };
            }),
          ],
        },
        onError: {
          target: "stateVerification",
          actions: [
            "saveError",
            // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
            assign(({ context, event }: any) => {
              const error = event.error;
              const patientName = [context.firstName, context.lastName]
                .filter(Boolean)
                .join(" ");
              return {
                ocrPrimaryBoundingBoxes: error?.boundingBoxes ?? null,
                ocrPrimaryQueryResults: error?.queryResults ?? null,
                _primaryReviewUsed: true,
                insurances: {
                  ...context.insurances,
                  primaryInsurance: {
                    ...context.insurances?.primaryInsurance,
                    subscriberName:
                      context.insurances?.primaryInsurance?.subscriberName ||
                      patientName,
                  },
                },
              };
            }),
          ],
        },
      },
    },

    saveInsuranceOcrToContext: {
      entry: assign({
        insurances: ({ context }) => {
          const ocrData = context.ocrPrimaryInsurance;

          if (!ocrData?.success) {
            return context.insurances;
          }

          const memberId = ocrData["What is the member ID?"] as
            | string
            | undefined;

          return {
            ...context.insurances,
            primaryInsurance: {
              ...context.insurances?.primaryInsurance,
              payer: context.insurances?.primaryInsurance?.payer || "",
              memberId:
                (memberId ? memberId.replaceAll(" ", "") : null) ||
                context.insurances?.primaryInsurance?.memberId ||
                "",
              state: context.insurances?.primaryInsurance?.state || "",
            },
          };
        },
      }),
      always: [
        {
          // Storefront flow: show inputForm to confirm before returning
          guard: ({ context }) =>
            context.addingInsuranceFromStorefront === true,
          target: "inputForm",
        },
        {
          // OCR success → skip manual entry, go directly to secondary insurance question
          guard: ({ context }) => !!context.ocrPrimaryInsurance?.success,
          target: "secondaryInsuranceQuestion",
        },
        {
          // OCR didn't produce usable data - fall back to manual entry
          target: "inputForm",
        },
      ],
    },

    stateVerification: {
      meta: {
        componentName: "InsuranceOcrVerification",
        contentKey: "medrite.checkin.insurance-state-verification",
        metadata: {
          contextField: "state",
          fieldType: "state-dropdown",
          showPhoto: false,
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: {
          target: "payerVerification",
          actions: ["saveFormData"],
        },
        BACK: {
          target: "insuranceCardEntry",
        },
      },
    },

    payerVerification: {
      meta: {
        componentName: "InsuranceOcrVerification",
        contentKey: "medrite.checkin.insurance-payer-verification",
        metadata: {
          contextField: "payer",
          fieldType: "payer-dropdown",
          showPhoto: true,
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: {
          target: "insuredNameVerification",
          actions: ["saveFormData"],
        },
        BACK: {
          target: "stateVerification",
        },
      },
    },

    insuredNameVerification: {
      meta: {
        componentName: "InsuranceOcrVerification",
        contentKey: "medrite.checkin.insurance-insured-name-verification",
        metadata: {
          contextField: "subscriberName",
          fieldType: "text",
          showPhoto: true,
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: {
          target: "memberIdVerification",
          actions: [
            "saveFormData",
            // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
            assign(({ context }: any) => {
              const name =
                context.insurances?.primaryInsurance?.subscriberName ?? "";
              const parts = name.trim().split(/\s+/);
              const firstName = parts[0] ?? "";
              const lastName = parts.slice(1).join(" ") ?? "";
              return {
                insurances: {
                  ...context.insurances,
                  primaryInsurance: {
                    ...context.insurances?.primaryInsurance,
                    insuredFirstName: firstName,
                    insuredLastName: lastName,
                  },
                },
              };
            }),
          ],
        },
        BACK: {
          target: "payerVerification",
        },
      },
    },

    memberIdVerification: {
      meta: {
        componentName: "InsuranceOcrVerification",
        contentKey: "medrite.checkin.insurance-member-id-verification",
        metadata: {
          contextField: "memberId",
          fieldType: "text",
          showPhoto: true,
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: [
          {
            // Both insurances need review (from verification): go to secondary review next
            guard: ({ context }) => context._secondaryReviewNeeded === true,
            target: "secondaryStateVerification",
            actions: [
              "saveFormData",
              assign({
                _secondaryReviewNeeded: false,
                _secondaryReviewUsed: true,
              }),
            ],
          },
          {
            // Secondary photos already captured OR came from verification unverified path
            // → no need to ask about secondary again
            guard: ({ context }) =>
              !!context.secondaryInsurancePhotoFront ||
              context._unverifiedRedirectAttempted === true,
            target: "verifyingInsurance",
            actions: ["saveFormData"],
          },
          {
            // Default: ask about secondary insurance
            target: "secondaryInsuranceQuestion",
            actions: ["saveFormData"],
          },
        ],
        BACK: {
          target: "insuredNameVerification",
        },
      },
    },

    insuranceCardMemberIdForm: {
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.insurance-member-id-input",
        metadata: {
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: {
          target: "secondaryInsuranceQuestion",
          actions: ["saveFormData"],
        },
        BACK: {
          target: "insuranceCardEntry",
        },
      },
    },

    inputForm: {
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.insurance-input",
        metadata: {
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: {
          target: "secondaryInsuranceQuestion",
          actions: ["saveFormData"],
        },
        BACK: {
          target: "insuranceCardEntry",
        },
      },
    },

    // ===== SECONDARY INSURANCE QUESTION =====

    secondaryInsuranceQuestion: {
      meta: {
        componentName: "SecondaryInsuranceQuestion",
        contentKey: "medrite.checkin.secondary-insurance-question",
      },
      on: {
        SUBMIT: [
          {
            guard: "hasSecondaryInsurance",
            target: "secondaryInsuranceCardEntry",
            actions: ["saveFormData"],
          },
          {
            // User says NO to secondary insurance
            // Skip verification if already verified and inputs haven't changed (and not from storefront)
            guard: ({ context }) => {
              const isFromStorefront =
                context.addingInsuranceFromStorefront === true;
              return (
                !isFromStorefront &&
                isAtLeastPrimaryInsuranceActive(context) &&
                !hasInsuranceInputChanged(context)
              );
            },
            target: "done",
            actions: ["saveFormData", "clearSecondaryInsurance"],
          },
          {
            // Insurance needs verification
            target: "verifyingInsurance",
            actions: ["saveFormData", "clearSecondaryInsurance"],
          },
        ],
        BACK: { target: "inputForm" },
      },
    },

    // ===== SECONDARY INSURANCE INPUT (flattened from secondaryInsuranceInput compound) =====

    secondaryInsuranceCardEntry: {
      always: [
        {
          // Re-invocation: if secondary insurance already exists with photos, skip to confirm form
          guard: ({ context }) => {
            const insurances = context.insurances as
              | { secondaryInsurance?: { insuranceId?: string } }
              | undefined;
            return (
              !!insurances?.secondaryInsurance?.insuranceId &&
              !!context.secondaryInsurancePhotoFront &&
              !!context.secondaryInsurancePhotoBack
            );
          },
          target: "secondaryInputForm",
        },
      ],
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.secondary-insurance-card-entry",
        metadata: {
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: [
          {
            // QR mode → upload flow
            guard: ({ context, event }) =>
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              isQrMode.impl({ context, event } as any),
            target: "uploadSecondaryInsuranceFrontPhoto",
            actions: ["saveFormData"],
          },
          {
            // Kiosk mode → capture flow
            target: "secondaryFrontPhoto",
            actions: ["saveFormData"],
          },
        ],
        BACK: { target: "secondaryInsuranceQuestion" },
      },
    },

    uploadSecondaryInsuranceFrontPhoto: {
      meta: {
        componentName: "PhotoUpload",
        contentKey: "medrite.checkin.upload-insurance-photo",
        metadata: {
          cardType: "secondaryInsurance",
          side: "front",
        },
      },
      on: {
        CONFIRM: {
          target: "uploadSecondaryInsuranceBackPhoto",
          actions: ["savePhotoData"],
        },
        TAKE_PHOTO: {
          target: "secondaryFrontPhoto",
        },
        SKIP: {
          target: "secondaryInsuranceCardMemberIdForm",
        },
        BACK: {
          target: "secondaryInsuranceCardEntry",
        },
      },
    },

    uploadSecondaryInsuranceBackPhoto: {
      meta: {
        componentName: "PhotoUpload",
        contentKey: "medrite.checkin.upload-insurance-photo",
        metadata: {
          cardType: "secondaryInsurance",
          side: "back",
        },
      },
      on: {
        CONFIRM: {
          target: "secondaryProcessingOcrInsurance",
          actions: ["savePhotoData"],
        },
        TAKE_PHOTO: {
          target: "secondaryBackPhoto",
        },
        SKIP: {
          target: "secondaryInsuranceCardMemberIdForm",
        },
        BACK: {
          target: "uploadSecondaryInsuranceFrontPhoto",
        },
      },
    },

    secondaryFrontPhoto: {
      meta: {
        componentName: "PhotoCapture",
        contentKey: "medrite.checkin.photo-capture",
        metadata: {
          cardType: "secondaryInsurance",
          side: "front",
          autoSubmit: true,
        },
      },
      on: {
        CONFIRM: {
          target: "secondaryBackPhoto",
          actions: ["savePhotoData"],
        },
        SKIP: {
          target: "secondaryInsuranceCardMemberIdForm",
        },
        BACK: [
          {
            // In QR mode, go back to the upload screen
            guard: ({ context, event }) =>
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              isQrMode.impl({ context, event } as any),
            target: "uploadSecondaryInsuranceFrontPhoto",
          },
          {
            // If secondary insurance already exists (re-invocation), go back to secondary question
            guard: ({ context }) => {
              const insurances = context.insurances as
                | { secondaryInsurance?: { insuranceId?: string } }
                | undefined;
              return !!insurances?.secondaryInsurance?.insuranceId;
            },
            target: "secondaryInsuranceQuestion",
          },
          {
            target: "secondaryInsuranceCardEntry",
          },
        ],
      },
    },

    secondaryBackPhoto: {
      meta: {
        componentName: "PhotoCapture",
        contentKey: "medrite.checkin.photo-capture",
        metadata: {
          cardType: "secondaryInsurance",
          side: "back",
          autoSubmit: true,
        },
      },
      on: {
        CONFIRM: {
          target: "secondaryProcessingOcrInsurance",
          actions: ["savePhotoData"],
        },
        SKIP: {
          target: "secondaryInsuranceCardMemberIdForm",
        },
        BACK: [
          {
            // In QR mode, go back to the upload screen
            guard: ({ context, event }) =>
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              isQrMode.impl({ context, event } as any),
            target: "uploadSecondaryInsuranceBackPhoto",
          },
          { target: "secondaryFrontPhoto" },
        ],
      },
    },

    secondaryProcessingOcrInsurance: {
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.processing-insurance",
      },
      invoke: {
        id: "analyzeOcr",
        src: "analyzeOcr",
        input: ({ context }) => ({
          mode: "insurance" as const,
          imageBase64: context.secondaryInsurancePhotoFront,
          contextKey: "ocrSecondaryInsurance" as const,
          orgId: context.orgId,
          queries: [
            "What is the payer name?",
            "What is the member ID?",
            "What is the subscriber name?",
          ],
        }),
        onDone: {
          target: "secondarySaveInsuranceOcrToContext",
          actions: [
            "saveActorResult",
            // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
            assign(({ event }: any) => {
              const ocrData = event.output?.ocrSecondaryInsurance;
              return {
                ocrSecondaryBoundingBoxes: ocrData?.boundingBoxes ?? null,
              };
            }),
          ],
        },
        onError: {
          target: "secondaryStateVerification",
          actions: [
            "saveError",
            // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
            assign(({ context, event }: any) => {
              const error = event.error;
              const patientName = [context.firstName, context.lastName]
                .filter(Boolean)
                .join(" ");
              return {
                ocrSecondaryBoundingBoxes: error?.boundingBoxes ?? null,
                ocrSecondaryQueryResults: error?.queryResults ?? null,
                _secondaryReviewUsed: true,
                insurances: {
                  ...context.insurances,
                  secondaryInsurance: {
                    ...context.insurances?.secondaryInsurance,
                    subscriberName:
                      context.insurances?.secondaryInsurance?.subscriberName ||
                      patientName,
                  },
                },
              };
            }),
          ],
        },
      },
    },

    secondarySaveInsuranceOcrToContext: {
      entry: assign({
        insurances: ({ context }) => {
          const ocrData = context.ocrSecondaryInsurance;

          if (!ocrData?.success) {
            return context.insurances;
          }

          const memberId = ocrData["What is the member ID?"] as
            | string
            | undefined;

          return {
            ...context.insurances,
            secondaryInsurance: {
              ...context.insurances?.secondaryInsurance,
              payer: context.insurances?.secondaryInsurance?.payer || "",
              memberId:
                (memberId ? memberId.replaceAll(" ", "") : null) ||
                context.insurances?.secondaryInsurance?.memberId ||
                "",
              state: context.insurances?.secondaryInsurance?.state || "",
            },
          };
        },
      }),
      always: [
        {
          // OCR success → skip manual entry, go directly to insurance verification
          guard: ({ context }) => !!context.ocrSecondaryInsurance?.success,
          target: "verifyingInsurance",
        },
        {
          // OCR didn't produce usable data - fall back to manual entry
          target: "secondaryInputForm",
        },
      ],
    },

    secondaryStateVerification: {
      meta: {
        componentName: "InsuranceOcrVerification",
        contentKey: "medrite.checkin.insurance-state-verification",
        metadata: {
          contextField: "state",
          fieldType: "state-dropdown",
          showPhoto: false,
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: {
          target: "secondaryPayerVerification",
          actions: ["saveFormData"],
        },
        BACK: {
          target: "secondaryInsuranceCardEntry",
        },
      },
    },

    secondaryPayerVerification: {
      meta: {
        componentName: "InsuranceOcrVerification",
        contentKey: "medrite.checkin.insurance-payer-verification",
        metadata: {
          contextField: "payer",
          fieldType: "payer-dropdown",
          showPhoto: true,
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: {
          target: "secondaryInsuredNameVerification",
          actions: ["saveFormData"],
        },
        BACK: {
          target: "secondaryStateVerification",
        },
      },
    },

    secondaryInsuredNameVerification: {
      meta: {
        componentName: "InsuranceOcrVerification",
        contentKey: "medrite.checkin.insurance-insured-name-verification",
        metadata: {
          contextField: "subscriberName",
          fieldType: "text",
          showPhoto: true,
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: {
          target: "secondaryMemberIdVerification",
          actions: [
            "saveFormData",
            // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
            assign(({ context }: any) => {
              const name =
                context.insurances?.secondaryInsurance?.subscriberName ?? "";
              const parts = name.trim().split(/\s+/);
              const firstName = parts[0] ?? "";
              const lastName = parts.slice(1).join(" ") ?? "";
              return {
                insurances: {
                  ...context.insurances,
                  secondaryInsurance: {
                    ...context.insurances?.secondaryInsurance,
                    insuredFirstName: firstName,
                    insuredLastName: lastName,
                  },
                },
              };
            }),
          ],
        },
        BACK: {
          target: "secondaryPayerVerification",
        },
      },
    },

    secondaryMemberIdVerification: {
      meta: {
        componentName: "InsuranceOcrVerification",
        contentKey: "medrite.checkin.insurance-member-id-verification",
        metadata: {
          contextField: "memberId",
          fieldType: "text",
          showPhoto: true,
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: {
          target: "verifyingInsurance",
          actions: ["saveFormData"],
        },
        BACK: {
          target: "secondaryInsuredNameVerification",
        },
      },
    },

    secondaryInputForm: {
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.secondary-insurance-input",
        metadata: {
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: [
          {
            // Skip verification if already verified and inputs haven't changed (and not from storefront)
            // NOTE: Must use hasInsuranceInputChangedWithEvent because guards run BEFORE saveFormData
            guard: ({ context, event }) => {
              const isFromStorefront =
                context.addingInsuranceFromStorefront === true;
              return (
                !isFromStorefront &&
                isAtLeastPrimaryInsuranceActive(context) &&
                !hasInsuranceInputChangedWithEvent(context, event)
              );
            },
            target: "done",
            actions: ["saveFormData"],
          },
          {
            target: "verifyingInsurance",
            actions: ["saveFormData"],
          },
        ],
        SKIP: {
          target: "verifyingInsurance",
        },
        BACK: {
          target: "secondaryInsuranceCardEntry",
        },
      },
    },

    secondaryInsuranceCardMemberIdForm: {
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.secondary-insurance-member-id-input",
        metadata: {
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: {
          target: "verifyingInsurance",
          actions: ["saveFormData"],
        },
        BACK: {
          target: "secondaryInsuranceCardEntry",
        },
      },
    },

    // ===== INSURANCE VERIFICATION =====

    verifyingInsurance: {
      entry: ["restoreInsuranceIdsFromBackground", "clearInsuredPricing"],
      meta: {
        componentName: "LoadingSpinner",
        contentKey: "medrite.checkin.verifying-insurance",
      },
      invoke: {
        id: "verifyInsurance",
        src: "verifyInsurance",
        input: ({ context }) => {
          const insurances = context.insurances as
            | {
                primaryInsurance?: {
                  payerId: string;
                  payer: string;
                  memberId: string;
                  state: string;
                  subscriberName?: string;
                  insuredFirstName?: string;
                  insuredLastName?: string;
                };
                secondaryInsurance?: {
                  payerId: string;
                  payer: string;
                  memberId: string;
                  state: string;
                  subscriberName?: string;
                  insuredFirstName?: string;
                  insuredLastName?: string;
                };
              }
            | undefined;

          const insuranceList = [
            insurances?.primaryInsurance,
            insurances?.secondaryInsurance,
          ].filter((v): v is NonNullable<typeof v> => Boolean(v));

          return {
            consumerId: context.consumerId,
            firstName: context.firstName,
            lastName: context.lastName,
            dateOfBirth: context.dateOfBirth,
            insurances: insuranceList.map((ins) => {
              const parsed = parseSubscriberName(
                ins.subscriberName,
                context.firstName,
                context.lastName,
              );
              return {
                ...ins,
                insuredFirstName: ins.insuredFirstName ?? parsed.first,
                insuredLastName: ins.insuredLastName ?? parsed.last,
              };
            }),
            orgId: context.orgId,
            email: context.email,
          };
        },
        onDone: [
          {
            // If adding from storefront (and not recovering from error), return to storefront
            guard: ({ context }) => {
              return (
                context.addingInsuranceFromStorefront === true &&
                !context.insuranceError
              );
            },
            target: "done",
            actions: [
              "saveActorResult",
              assign({
                _returnToStorefront: true,
                addingInsuranceFromStorefront: false,
                insuranceError: undefined,
                insuranceInputSnapshot: ({ context }) =>
                  createInsuranceInputSnapshot(context),
                insuranceStates: ({ event }) => {
                  const output = event.output as
                    | { insuranceStates?: unknown }
                    | undefined;
                  return output?.insuranceStates || [];
                },
                pendingPricesMode: ({ event }) => {
                  const output = event.output as
                    | {
                        insuranceStates?: {
                          primary?: { withBenefits?: boolean };
                        };
                        pendingPricesMode?: boolean;
                      }
                    | undefined;
                  return derivePendingPricesMode(
                    output?.insuranceStates,
                    output?.pendingPricesMode,
                  );
                },
              }),
            ],
          },
          {
            // Insurance verified successfully - show success screen before exiting
            target: "insuranceSuccess",
            actions: [
              "saveActorResult",
              assign({
                insuranceInputSnapshot: ({ context }) =>
                  createInsuranceInputSnapshot(context),
                insuranceStates: ({ event }) => {
                  const output = event.output as
                    | {
                        insuranceStates?: {
                          primary?: {
                            inNetwork: boolean;
                            active: boolean;
                            withBenefits: boolean;
                            verified: boolean;
                          };
                          secondary?: {
                            inNetwork: boolean;
                            active: boolean;
                            withBenefits: boolean;
                            verified: boolean;
                          };
                        };
                      }
                    | undefined;
                  return output?.insuranceStates;
                },
                pendingPricesMode: ({ event }) => {
                  const output = event.output as
                    | {
                        insuranceStates?: {
                          primary?: { withBenefits?: boolean };
                        };
                        pendingPricesMode?: boolean;
                      }
                    | undefined;
                  return derivePendingPricesMode(
                    output?.insuranceStates,
                    output?.pendingPricesMode,
                  );
                },
              }),
            ],
          },
        ],
        onError: [
          {
            // Branch 1: Primary unverified, primary review slot available
            // → send to primary review; if secondary also unverified, flag it
            guard: ({ context, event }) => {
              const error = event.error as
                | { insuranceStatuses?: Array<{ status?: string }> }
                | undefined;
              const isPrimaryUnverified =
                error?.insuranceStatuses?.[0]?.status?.toLowerCase() ===
                "unverified";
              return isPrimaryUnverified && !context._primaryReviewUsed;
            },
            target: "stateVerification",
            actions: [
              // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
              assign(({ context, event }: any) => {
                const error = event.error;
                const isSecondaryUnverified =
                  error?.insuranceStatuses?.[1]?.status?.toLowerCase() ===
                  "unverified";
                const patientName = [context.firstName, context.lastName]
                  .filter(Boolean)
                  .join(" ");
                return {
                  _primaryReviewUsed: true,
                  _unverifiedRedirectAttempted: true,
                  _secondaryReviewNeeded:
                    isSecondaryUnverified && !context._secondaryReviewUsed,
                  insurances: {
                    ...context.insurances,
                    primaryInsurance: {
                      ...context.insurances?.primaryInsurance,
                      subscriberName:
                        context.insurances?.primaryInsurance?.subscriberName ||
                        patientName,
                    },
                  },
                };
              }),
            ],
          },
          {
            // Branch 2: Secondary unverified only (primary is fine or already reviewed),
            // secondary review slot available → send directly to secondary review
            guard: ({ context, event }) => {
              const error = event.error as
                | { insuranceStatuses?: Array<{ status?: string }> }
                | undefined;
              const isPrimaryUnverified =
                error?.insuranceStatuses?.[0]?.status?.toLowerCase() ===
                "unverified";
              const isSecondaryUnverified =
                error?.insuranceStatuses?.[1]?.status?.toLowerCase() ===
                "unverified";
              return (
                isSecondaryUnverified &&
                !context._secondaryReviewUsed &&
                (!isPrimaryUnverified || !!context._primaryReviewUsed)
              );
            },
            target: "secondaryStateVerification",
            actions: [
              // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
              assign(({ context }: any) => {
                const patientName = [context.firstName, context.lastName]
                  .filter(Boolean)
                  .join(" ");
                return {
                  _secondaryReviewUsed: true,
                  insurances: {
                    ...context.insurances,
                    secondaryInsurance: {
                      ...context.insurances?.secondaryInsurance,
                      subscriberName:
                        context.insurances?.secondaryInsurance
                          ?.subscriberName || patientName,
                    },
                  },
                };
              }),
            ],
          },
          {
            // Branch 3: All applicable review slots used, OON, or inactive → failure screen
            guard: ({ context, event }) => {
              const error = event.error as
                | {
                    code?: string;
                    insuranceStatuses?: Array<{ status?: string }>;
                    insuranceStates?: {
                      primary?: { active?: boolean };
                      secondary?: { active?: boolean };
                    };
                  }
                | undefined;

              const isPrimaryUnverified =
                error?.insuranceStatuses?.[0]?.status?.toLowerCase() ===
                "unverified";
              const isSecondaryUnverified =
                error?.insuranceStatuses?.[1]?.status?.toLowerCase() ===
                "unverified";
              const isAnyUnverified =
                isPrimaryUnverified || isSecondaryUnverified;
              const reviewsExhausted =
                (!isPrimaryUnverified || !!context._primaryReviewUsed) &&
                (!isSecondaryUnverified || !!context._secondaryReviewUsed);

              const isOON = error?.code === "OUT_OF_NETWORK";
              const isInactive =
                error?.insuranceStates?.primary?.active === false;
              // Primary active but secondary inactive or OON
              const isSecondaryOnlyFailure =
                error?.insuranceStates?.primary?.active === true &&
                error?.insuranceStates?.secondary?.active === false;

              return (
                (isAnyUnverified && reviewsExhausted) ||
                isOON ||
                isInactive ||
                isSecondaryOnlyFailure
              );
            },
            target: "insuranceVerificationFailure",
            actions: ["saveError", "assignInsuranceErrorToContext"],
          },
          {
            target: "insuranceVerificationError",
            actions: ["saveError", "assignInsuranceErrorToContext"],
          },
        ],
      },
    },

    // ===== INSURANCE VERIFICATION ERROR + RETRY STATES =====

    insuranceVerificationError: {
      meta: {
        componentName: "InsuranceErrorDisplay",
        contentKey: "medrite.checkin.insurance-verification-error",
      },
      on: {
        RETRY: [
          {
            guard: "isNameError",
            target: "retryInsuranceName",
          },
          {
            guard: "isDobError",
            target: "retryInsuranceDob",
          },
          {
            guard: "isSecondaryMemberIdError",
            target: "editSecondaryInsuranceRetry",
          },
          {
            guard: "isMemberIdError",
            target: "editInsuranceRetry",
          },
          {
            guard: "isSecondaryInsuranceError",
            target: "editSecondaryInsuranceRetry",
          },
          {
            guard: "isOON",
            target: "oonProceedWithSelfPay",
          },
          {
            target: "editInsuranceRetry",
          },
        ],
        SKIP: {
          target: "done",
        },
      },
    },

    oonProceedWithSelfPay: {
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.oon-insurance",
        metadata: {
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: {
          target: "done",
        },
        BACK: { target: "insuranceCardEntry" },
      },
    },

    editInsuranceRetry: {
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.edit-insurance-retry",
        metadata: {
          isPrimary: true,
        },
      },
      on: {
        SUBMIT: {
          target: "verifyingInsurance",
          actions: [
            "saveFormData",
            assign({
              insuranceError: undefined,
            }),
          ],
        },
        SKIP: {
          target: "done",
        },
        BACK: { target: "insuranceVerificationError" },
      },
    },

    editSecondaryInsuranceRetry: {
      meta: {
        componentName: "InsuranceInput",
        contentKey: "medrite.checkin.edit-secondary-insurance-retry",
        metadata: {
          isPrimary: false,
        },
      },
      on: {
        SUBMIT: {
          target: "verifyingInsurance",
          actions: ["saveFormData"],
        },
        SKIP: {
          target: "done",
        },
        BACK: { target: "insuranceVerificationError" },
      },
    },

    retryInsuranceName: {
      meta: {
        componentName: "FullNameInput",
        contentKey: "medrite.checkin.full-name-input",
      },
      on: {
        SUBMIT: {
          target: "verifyingInsurance",
          actions: ["saveFormData"],
        },
        SKIP: {
          target: "done",
        },
        BACK: { target: "insuranceVerificationError" },
      },
    },

    retryInsuranceDob: {
      meta: {
        componentName: "DOBInput",
        contentKey: "medrite.checkin.dob-input",
      },
      on: {
        SUBMIT: {
          target: "verifyingInsurance",
          actions: ["saveFormData"],
        },
        SKIP: [
          {
            // If adding from storefront, complete arc with returnToStorefront flag
            guard: ({ context }) =>
              context.addingInsuranceFromStorefront === true,
            target: "done",
            actions: [
              assign({
                _returnToStorefront: true,
                addingInsuranceFromStorefront: false,
              }),
            ],
          },
          {
            // Otherwise, also return to storefront (original logic had both branches go to storefrontBrowse)
            target: "done",
            actions: [
              assign({
                _returnToStorefront: true,
              }),
            ],
          },
        ],
        BACK: { target: "insuranceVerificationError" },
      },
    },

    // ===== INSURANCE VERIFICATION FAILURE =====
    // Shown for: second-time unverified, out-of-network, or inactive insurance.
    // Four resolution paths: back to input, front desk, try another card, skip insurance.

    insuranceVerificationFailure: {
      meta: {
        componentName: "InsuranceVerificationFailure",
        contentKey: "medrite.checkin.insurance-verification-failure",
      },
      on: {
        BACK: [
          {
            // If the secondary insurance was the failing one, go back to its form
            guard: "isSecondaryInsuranceError",
            target: "secondaryInputForm",
          },
          {
            target: "inputForm",
          },
        ],
        FRONT_DESK: {
          // Signal parent to route to front-desk completion screen (with insurance data)
          actions: sendParent(({ context }) => ({
            type: "FRONT_DESK",
            data: {
              ...extractInsuranceOutput(context),
              failureType: deriveFrontDeskFailureType(context),
            },
          })),
        },
        RETRY: [
          {
            // If secondary insurance was the failing one, only restart secondary entry
            guard: "isSecondaryInsuranceError",
            target: "secondaryInsuranceCardEntry",
            actions: ["clearSecondaryInsurance"],
          },
          {
            // Otherwise, restart from primary insurance entry
            target: "insuranceCardEntry",
            actions: ["clearInsurance"],
          },
        ],
        SKIP: [
          {
            // If secondary was the failing one, only clear secondary — keep primary
            guard: "isSecondaryInsuranceError",
            target: "done",
            actions: ["clearSecondaryInsurance"],
          },
          {
            // Otherwise, clear everything — continue without insurance
            target: "done",
            actions: ["clearInsurance"],
          },
        ],
        NEXT: {
          // Unverified: preserve insurance, proceed with pending prices
          target: "done",
          actions: [assign({ pendingPricesMode: true })],
        },
      },
    },

    // ===== INSURANCE SUCCESS =====
    insuranceSuccess: {
      meta: {
        componentName: "InsuranceVerificationSuccess",
        contentKey: "medrite.checkin.insurance-verification-success",
      },
      after: {
        3000: {
          target: "done",
        },
      },
    },

    // ===== EXIT =====
    done: {
      type: "final" as const,
    },
  },
});
